import { useContext } from 'react';
import { Card, Spin } from 'antd';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LabelList } from 'recharts';
import { IDashboardRegistrationHistoryItem } from '../../../../services/http/mappers/DashboardMapper';
import { RegistrationPeriod } from '../../useDashboard';
import { ThemeContext } from '../../../../contexts/ThemeContext';

interface RegistrationHistoryChartProps {
    data: IDashboardRegistrationHistoryItem[];
    isLoading: boolean;
    period: RegistrationPeriod;
    onPeriodChange: (period: RegistrationPeriod) => void;
    showLabels?: boolean;
    isRaffleFiltered?: boolean;
}

const PERIODS: { key: RegistrationPeriod; label: string }[] = [
    { key: 'day', label: 'Dia' },
    { key: 'month', label: 'Mês' },
    { key: 'year', label: 'Ano' },
];

const PERIOD_SUBTITLE: Record<RegistrationPeriod, string> = {
    day: 'Últimos 15 dias',
    month: 'Últimos 12 meses',
    year: 'Evolução anual de novos cadastros',
};

const formatNumber = (value: number) => new Intl.NumberFormat('pt-BR').format(value);
const formatMonthLabel = (label: string) =>
    /^\d{2}\/\d{4}$/.test(label) ? `${label.slice(0, 3)}${label.slice(5)}` : label;

export default function RegistrationHistoryChart({
    data,
    isLoading,
    period,
    onPeriodChange,
    showLabels = false,
    isRaffleFiltered = false,
}: RegistrationHistoryChartProps) {
    const { theme } = useContext(ThemeContext);

    const currentSubtitle = isRaffleFiltered
        ? (period === 'day' ? 'Cadastros diários no período do sorteio' : PERIOD_SUBTITLE[period])
        : PERIOD_SUBTITLE[period];

    return (
        <Card
            title={
                <div style={{ paddingTop: 16 }}>
                    <div style={{ fontWeight: 700, fontSize: 20 }}>Histórico de cadastros</div>
                    <div style={{ fontWeight: 400, fontSize: 16, color: '#888' }}>{currentSubtitle}</div>
                </div>
            }
            extra={
                <div style={{ display: 'flex', gap: 4 }}>
                    {PERIODS.map(({ key, label }) => (
                        <button
                            key={key}
                            onClick={() => onPeriodChange(key)}
                            style={{
                                padding: '4px 12px',
                                borderRadius: 6,
                                border: '1px solid',
                                borderColor: period === key ? '#1677ff' : (theme === 'light' ? '#d9d9d9' : '#434343'),
                                background: period === key ? '#1677ff' : (theme === 'light' ? '#fff' : '#1f1f1f'),
                                color: period === key ? '#fff' : (theme === 'light' ? '#555' : '#aaa'),
                                fontSize: 18,
                                fontWeight: period === key ? 600 : 400,
                                cursor: 'pointer',
                            }}
                        >
                            {label}
                        </button>
                    ))}
                </div>
            }
            style={{ background: theme === 'light' ? '#FFFFFF' : '#1f1f1f', borderRadius: 16, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', border: 'none', flex: 1 }}
            bodyStyle={{ padding: 20 }}
        >
            {isLoading ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
                    <Spin />
                </div>
            ) : (
                <ResponsiveContainer width="100%" height={300}>
                    <AreaChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                        <defs>
                            <linearGradient id="colorTotal" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#52c41a" stopOpacity={0.25} />
                                <stop offset="95%" stopColor="#52c41a" stopOpacity={0} />
                            </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={theme === 'light' ? '#d0d0d0' : '#303030'} />
                        <XAxis dataKey="label" axisLine={false} tickLine={false} style={{ fontSize: 14 }} tick={{ fill: theme === 'light' ? '#444' : '#aaa' }} tickFormatter={formatMonthLabel} />
                        <YAxis axisLine={false} tickLine={false} style={{ fontSize: 14 }} tick={{ fill: theme === 'light' ? '#444' : '#aaa' }} allowDecimals={false} />
                        <Tooltip
                            formatter={(value: number) => [formatNumber(value), 'Novos cadastros']}
                            contentStyle={theme === 'dark' ? { backgroundColor: '#1f1f1f', borderColor: '#303030', color: '#e8e8e8' } : undefined}
                            itemStyle={theme === 'dark' ? { color: '#e8e8e8' } : undefined}
                        />
                        <Area
                            type="monotone"
                            dataKey="total"
                            stroke="#52c41a"
                            strokeWidth={2}
                            fill="url(#colorTotal)"
                            dot={showLabels ? { r: 3, fill: '#52c41a', strokeWidth: 0 } : false}
                            activeDot={{ r: 4, strokeWidth: 0 }}
                            isAnimationActive={!showLabels}
                            animationDuration={600}
                        >
                            {showLabels && (
                                <LabelList dataKey="total" position="top" formatter={(v: unknown) => formatNumber(v as number)} fill="#888" style={{ fontSize: 10, fontWeight: 600 }} />
                            )}
                        </Area>
                    </AreaChart>
                </ResponsiveContainer>
            )}
        </Card>
    );
}
